import { Injectable } from '@angular/core';
import { ValidationError } from '@core-modules/components/core-forms/validation/validation-error';
import { CoreHttpClient } from '@core-modules/providers/core-http-client/core-http-client';
import { DeviceProvider } from '@core-modules/providers/device/device';
import { EnvironmentProvider } from '@core-modules/providers/environment-variables/environment';
import { AuthProvider } from 'authorization/providers/auth/auth-provider';
import { ApplicationVersionResult } from '../models/application-version-result';
import { ContentPageDetails } from '../models/content-page';
import { Currency } from '../models/currency';
import { FaqSet } from '../models/faq-set';
import { MobileUserSettings } from '../models/mobile-user-settings';
/*
  Generated class for the UserSettingsProvider provider.

  See https://angular.io/docs/ts/latest/guide/dependency-injection.html
  for more info on providers and Angular DI.
*/

@Injectable()
export class UserSettingsProvider
{
    //#region Constructor

    /**
     * Class constructor
     * @param http
     * @param auth
     * @param environment
     * @param device
     */
    constructor
        (
            private http: CoreHttpClient,
            private auth: AuthProvider,
            private environment: EnvironmentProvider,
            private device: DeviceProvider
        )
    {
    }

    //#endregion

    
    //#region Methods - Currency Methods

    /**
     * Get a list of preferred currency options
     */
    public getCurrencies(): Promise<Currency[]>
    {
        return this.http.get<Currency[]>(`${this.environment.apiEndpoint}/mobileapp/Settings/GetPreferredCurrencyOptions`)
    }

    /**
    * Update the user's currency preferences
    * @param currencyID
    */
    public async updatePreferredCurrency(currencyID): Promise<ValidationError[]>
    {
        // get the user's settings and identity
        let userIdentity = await this.auth.getUserIdentity()

        // prepare the currency
        let currencyObj = {
            preferredCurrency: currencyID
        };

        // post the changes to the server
        let errors = await this.http.post<ValidationError[]>(`${this.environment.apiEndpoint}/mobileapp/Settings/SetPreferredCurrency/${userIdentity.sessionToken}`, currencyObj);
        return Promise.resolve(errors);
    }

    //#endregion

    //#region Methods - User Preferences

    /**  
     * Get the user's mobile settings
     * @param {string} sessionToken ID of the client
     * @returns
     */
    public async getMobileUserSettings(sessionToken: string): Promise<MobileUserSettings>
    {
        return this.http.get<MobileUserSettings>(`${this.environment.apiEndpoint}/mobileapp/Settings/GetMobileUserSettings/${sessionToken}`)
    }

    //#endregion

    //#region Methods -  Client Profile Picture

    /**
     * Mobile implementation for the update profile picture method
     * @param file fileURI or file object
     */
    public async updateProfilePicture(file: File | string): Promise<ValidationError[]>
    {
        // get the current user's identity
        let userIdentity = await this.auth.getUserIdentity();
        return this.http.postFile<ValidationError[]>(`${this.environment.apiEndpoint}/mobileapp/Settings/SetProfilePicture/${userIdentity.sessionToken}`, 'profilePicture', file);
    }

    //#endregion


    //#region Methods - Fingerprint Login

    /**
     * Update the biometric preferences for the client
     * @param sessionToken 
     * @param value 
     */
    public async setBiometricsLogin(sessionToken: string, value: boolean)
    {
        let requestObject = {
            enableLogin: (value ? 'true' : 'false')
        };

        // perform the request
        let result = await this.http.post(`${this.environment.apiEndpoint}/mobileapp/Settings/SetBiometricLogin/${sessionToken}`, requestObject)
        // resolve the promise
        return Promise.resolve(result);
    }

    //#endregion

    //#region Methods - Content Pages

    /**
     * Retrieve the content for the terms and conditions page
     */
    public async getTermsAndConditionsContent(): Promise<ContentPageDetails>
    {
        // performt the request
        let content = await this.http.get<ContentPageDetails>(`${this.environment.apiEndpoint}/mobileapp/content/GetTermsAndConditionsPage`)

        // resolve the promise
        return Promise.resolve(content);
    }

    /**
     * Retrieve the content for the about deVere core page
     */
    public async getAboutDevereCoreContent(): Promise<ContentPageDetails>
    {
        // perform the request
        let content = await this.http.get<ContentPageDetails>(`${this.environment.apiEndpoint}/mobileapp/content/GetAboutDevereCorePage`)

        // resolve the content
        return Promise.resolve(content);
    }

    /**
     * Retrieve the content for the about deVere core page
     */
    public async getPrivacyPolicyContent(): Promise<ContentPageDetails>
    {
        // fetch the content
        let content = await this.http.get<ContentPageDetails>(`${this.environment.apiEndpoint}/mobileapp/content/GetPrivacyPolicyPage`)

        // resolve the promise
        return Promise.resolve(content);
    }

    /**
     * Get the set of frequently asked questions
     */
    public getFrequentlyAskedQuestions(): Promise<FaqSet>
    {
        // fetch the content
        let content = this.http.get<FaqSet>(`${this.environment.apiEndpoint}/mobileapp/content/GetFaqSet`)

        // resolve the promise
        return Promise.resolve(content);
    }

    //#endregion


    //#region Methods - PIN Code

    /**
     * Update pin
     * @param oldPin
     * @param newPin
     */
    public async setPINNumber(oldPin: string, newPin: string): Promise<any>
    {
        // prepare the request object
        let requestObject = {
            oldPin: oldPin,
            newPin: newPin
        };

        let userIdentity = await this.auth.getUserIdentity();
        // perform the request
        return this.http.post(`${this.environment.apiEndpoint}/mobileapp/Settings/SetPinNumber/${userIdentity.sessionToken}`, requestObject)
    }

    //#endregion

    //#region Methods - App Versioning

    /**
     * Check if the current application's version is
     * still supported
     */
    public async checkApplicationVerison(): Promise<ApplicationVersionResult>
    {
        // if not running on a mobile device
        // simply resolve the promise
        if (!this.device.isApplication())
        {
            // if it's web, skip the check and resolve
            let browserResult: ApplicationVersionResult = {
                VersionValid: true,
                AppStoreUrl: null,
                Message: null
            };

            return Promise.resolve(browserResult);
        }

        // initialize the platform variable
        let platform = this.device.getDeviceClientType();

        // get the app's version number
        let version = this.environment.appVersion;

        // prepare the request object
        let requestObject = {
            version: version,
            platform: platform,
            osVersion: this.device.getDeviceSoftwareVersion()
        };

        // fetch the content
        let result = await this.http.post<ApplicationVersionResult>(`${this.environment.apiEndpoint}/mobileapp/settings/CheckApplicationVersion`, requestObject)

        // resolve the promise
        return Promise.resolve(result);
    }

    //#endregion
}
